import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';

import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { containerStyles } from '../../styles/layout';
import { headers } from '../../styles/type';
import { shadow } from '../../styles/box';

import Image from '../Image';
import Link from '../Link';
import TextRenderer from '../TextRenderer';

const Container = styled.div`
  padding: ${rem(32)} 0;
  background-color : ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
  `}

  ${({ loopAnimation }) => loopAnimation && `
    overflow: hidden;
  `}

  ${media('>tiny')} {
    padding: ${rem(40)} 0;
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Headline = styled.h2`
  ${headers.h5}
  text-align: center;
  margin-bottom: ${rem(40)};
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `};
`;

const AnimLoop = keyframes`
  from { transform: translate(0%) }
  to { transform: translate(-50%) }
`;

const GridContainer = styled.div`

  ${media('>large')} {
    --page-gutter: calc((100vw - ${rem(1300)}) / 2);
    --fade-size: ${rem(120)};
    mask: linear-gradient(
      to right,
      transparent,
      transparent var(--page-gutter),
      black calc(var(--page-gutter) + var(--fade-size)),
      black calc(100% - var(--page-gutter) - var(--fade-size)),
      transparent calc(100% - var(--page-gutter)),
      transparent
    );
  }
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  ${({ loopAnimation }) => loopAnimation && css`
    flex-wrap: nowrap;
    width: max-content;
    animation: ${AnimLoop} 24s linear infinite;
  `}
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: ${rem(12)} ${rem(12)};

  ${media('>tiny')} {
    margin: ${rem(24)} ${rem(24)};
  }

  ${media('>small')} {
    margin: ${rem(32)} ${rem(32)};
  }

  ${media('>medium')} {
    margin: ${rem(48)} ${rem(48)};
  }

  ${({ boxed, theme }) => boxed && css`
    ${shadow}
    border-radius: ${rem(10)};
    padding: ${rem(8)} ${rem(16)};

    ${theme.pageOverrides?.includes('Dark Mode') && `
      border: 1px solid ${colors.blue['700']};
      box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.16), 0px 2px 8px 0px rgba(0, 0, 0, 0.16);
    `}
  `}
`;

const Logo = styled(Image)`
  width: auto;
  max-height: ${rem(54)};
  ${({ grayscale }) => grayscale && `
    filter: grayscale(1);
  `}
`;

const LogoList = ({
  headline,
  logoItems,
  boxedLogos,
  grayscaleLogos,
  loopAnimation,
  backgroundColor
}) => {
  const renderedLogoItems = loopAnimation
    ? [...logoItems, ...logoItems, ...logoItems, ...logoItems]
    : logoItems;

  return (
    <Container backgroundColor={backgroundColor} loopAnimation={loopAnimation}>
      <ContentWrapper>
        {headline && (
          <Headline>
            <TextRenderer text={headline} />
          </Headline>
        )}
      </ContentWrapper>
      <GridContainer>
        <ContentWrapper>
          {logoItems
            && (
              <Grid
                loopAnimation={loopAnimation}
              >
                {renderedLogoItems.map((logoItem, index) => (
                  <LogoWrapper
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    boxed={boxedLogos}
                  >
                    {logoItem.link ? (
                      <Link to={logoItem.link}>
                        <Logo
                          image={logoItem.logo}
                          alt={logoItem.logo?.description}
                          grayscale={grayscaleLogos}
                        />
                      </Link>
                    ) : (
                      <Logo
                        image={logoItem.logo}
                        alt={logoItem.logo?.description}
                        grayscale={grayscaleLogos}
                      />
                    )}
                  </LogoWrapper>
                ))}
              </Grid>
            )}
        </ContentWrapper>
      </GridContainer>
    </Container>
  );
};

LogoList.propTypes = {
  headline: PropTypes.string,
  boxedLogos: PropTypes.bool,
  grayscaleLogos: PropTypes.bool,
  loopAnimation: PropTypes.bool,
  backgroundColor: PropTypes.oneOf(['Blue', 'White']),
  logoItems: PropTypes.arrayOf(
    PropTypes.shape({
      logo: PropTypes.shape({
        // eslint-disable-next-line react/forbid-prop-types
        gatsbyImageData: PropTypes.object,
        description: PropTypes.string
      }),
      link: PropTypes.string
    })
  )
};

// The height was 48px but I changed it to 72px to address concerns from Clint and Shannon
export const query = graphql`
  fragment LogoListFragment on ContentfulLogoList {
    id
    __typename
    headline
    boxedLogos
    grayscaleLogos
    loopAnimation
    backgroundColor
    logoItems {
      logo {
        gatsbyImageData(height: 54, quality: 75)
        ...ImageAssetFragment
      }
      link
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default LogoList;
